import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Stack,
  Button,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Card,
  CardContent,
  Box
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { usePermissions } from '../../utils/permissions';
import { supabase } from '../../supabaseClient';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { HeaderCard, SectionPaper } from './accounting-overview-components/layout';

const scenarios = [
  { name: 'Base Plan', cash: '$2.4M', runway: '9.8 mo', note: 'Meets board guidance', status: 'healthy' },
  { name: 'Aggressive Hiring', cash: '$1.6M', runway: '6.1 mo', note: 'Adds 12 FTE to R&D', status: 'watch' },
  { name: 'Wholesale Push', cash: '$2.9M', runway: '11.2 mo', note: 'Inventory build in Feb', status: 'healthy' }
];

const horizon = [
  { label: '30 days', value: '$2.63M', detail: 'Includes $410k AR inflow' },
  { label: '60 days', value: '$2.84M', detail: 'Inventory restock in mid-Jan' },
  { label: '90 days', value: '$2.71M', detail: 'Capex for new fill line' }
];

const alerts = [
  { title: 'Working capital', detail: 'Inventory build pushes cycle to 62 days in Feb', level: 'watch' },
  { title: 'FX exposure', detail: 'EUR receivables exceed hedge policy by $18k', level: 'watch' },
  { title: 'Capex draw', detail: 'Production line upgrade scheduled March 3', level: 'healthy' },
  { title: 'Credit facility', detail: '$1.5M availability unused', level: 'healthy' }
];

const AccountingForecastPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const { canViewAccountingForecast } = usePermissions();
  const [companyName, setCompanyName] = useState('');

  const fetchCompanyData = async () => {
    if (!companyId) return;
    try {
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();
      
      if (companyError) throw companyError;
      setCompanyName(companyData?.name || 'Company');
    } catch (err) {
      console.error('Error fetching company data:', err);
      setCompanyName('Company Name');
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchCompanyData();
    }
  }, [companyId]);

  // Permission check
  if (!canViewAccountingForecast()) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <ShowChartIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Forecast Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Forecast. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  const getStatusChip = (status) => {
    const config =
      status === 'watch'
        ? { color: theme.palette.warning.main, label: 'Monitor' }
        : { color: theme.palette.success.main, label: 'On track' };
    return (
      <Chip
        size="small"
        label={config.label}
        sx={{
          borderRadius: 2,
          borderColor: config.color,
          color: config.color
        }}
        variant="outlined"
      />
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4, pb: 10 }}>
      <HeaderCard sx={{ mb: 4 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="center" sx={{ p: 4 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.03)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}
          >
            <ShowChartIcon sx={{ fontSize: 36, color: theme.palette.primary.main }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 400, color: 'primary.main', mb: 0.5 }}>
              Forecast
            </Typography>
            <Typography variant="subtitle1" sx={{ 
              color: 'text.secondary', 
              fontWeight: 500,
              fontSize: '1rem',
              mb: 1
            }}>
              {companyName}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Scenario planning and cash trajectory snapshots. The layout mirrors dashboard analytics so finance and ops can speak the same language.
            </Typography>
          </Box>
          <Button variant="contained" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 400 }}>
            Export Model
          </Button>
        </Stack>
      </HeaderCard>

      <SectionPaper sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 400, mb: 2 }}>
          Scenario Planner
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Scenario</TableCell>
              <TableCell>Cash Position</TableCell>
              <TableCell>Runway</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {scenarios.map((scenario) => (
              <TableRow key={scenario.name} hover>
                <TableCell>
                  <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                    {scenario.name}
                  </Typography>
                </TableCell>
                <TableCell>{scenario.cash}</TableCell>
                <TableCell>{scenario.runway}</TableCell>
                <TableCell>{scenario.note}</TableCell>
                <TableCell>{getStatusChip(scenario.status)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionPaper>

      <Grid container spacing={3} sx={{ mb: 1 }}>
        <Grid item xs={12} md={5}>
          <SectionPaper>
            <Typography variant="h6" sx={{ fontWeight: 400, mb: 2 }}>
              Cash Trajectory
            </Typography>
            <Stack spacing={1.5}>
              {horizon.map((item) => (
                <Card
                  key={item.label}
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                    p: 2
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                      Next {item.label}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {item.value}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {item.detail}
                  </Typography>
                </Card>
              ))}
            </Stack>
          </SectionPaper>
        </Grid>
        <Grid item xs={12} md={7}>
          <SectionPaper>
            <Typography variant="h6" sx={{ fontWeight: 400, mb: 2 }}>
              Automated Alerts
            </Typography>
            <Grid container spacing={2}>
              {alerts.map((alert) => (
                <Grid item xs={12} md={6} key={alert.title}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: `1px solid ${alpha(
                        alert.level === 'watch' ? theme.palette.warning.main : theme.palette.success.main,
                        0.4
                      )}`,
                      background: alpha(
                        alert.level === 'watch' ? theme.palette.warning.main : theme.palette.success.main,
                        0.08
                      ),
                      p: 2,
                      height: '100%'
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      {alert.level === 'watch' ? (
                        <WarningAmberIcon color="warning" />
                      ) : (
                        <CheckCircleIcon color="success" />
                      )}
                      <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                        {alert.title}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {alert.detail}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </SectionPaper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AccountingForecastPage;

